import { useState } from 'react';
import { CONSUMER_CARE_SETTINGS, DEFAULT_CONSUMER_CARE_SETTING } from '@shared/consumerPlan';
import { ChoiceList } from '../components/choice-list';
import { StepFrame } from '../components/step-frame';
import { useCarePlanEditor } from '../hooks/use-care-plan-editor';
import { useConsumerClient } from '../hooks/use-consumer-client';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { useConsumerPlan } from '../hooks/use-consumer-plan';
import { CARE_SETTING_COPY } from '../lib/copy';
import type { ConsumerStepProps } from './step-props';

export function SettingStep({ clientId }: ConsumerStepProps) {
  const { goTo } = useConsumerFlow(clientId);
  const { plan, updatePlan } = useConsumerPlan(clientId);
  const { client, scenario } = useConsumerClient(clientId);
  const { saveScenario } = useCarePlanEditor(client);
  const [setting, setSetting] = useState<ConsumerCareSetting>(
    scenario?.careSetting ?? plan?.careSetting ?? DEFAULT_CONSUMER_CARE_SETTING,
  );

  const handleContinue = () => {
    if (scenario?.careSetting !== setting) saveScenario({ careSetting: setting });
    updatePlan({ careSetting: setting });
    goTo('household');
  };

  return (
    <StepFrame
      step="setting"
      title="Where would you like to receive care?"
      subtitle="Choose the setting you’d like to plan for. You can change it later and see how the same coverage performs."
      help={
        <>
          <p>Start with your preference today. You can change the scenario later and see how the same policy performs.</p>
          <p>“Start at home” allows a move if you need more hands-on help. “Stay at home” models paid support at home throughout.</p>
        </>
      }
      primary={{ label: 'Continue', onClick: handleContinue }}
      next="confirm who you’d like to cover."
    >
      <ChoiceList
        label="Where care happens"
        value={setting}
        onChange={setSetting}
        choices={CONSUMER_CARE_SETTINGS.map(value => ({
          value,
          ...CARE_SETTING_COPY[value],
          badge: value === DEFAULT_CONSUMER_CARE_SETTING ? 'Most popular' : undefined,
        }))}
      />
    </StepFrame>
  );
}
